import React, { useContext } from "react";
import LinkedTravelRows from "./linkedtravelrows";
import { getLinkedTravelInfo } from "./linkedtravelrow";
import { useCustomQuery } from "utilities/hookutils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { Panel } from "components/panels";
import { ButtonSecondary } from "components/Atoms/Button";
import { PhosphorAirplaneTilt, PhosphorCar, PhosphorTrain } from "components/Atoms/PhosphorIcon";
import { getRealStartEndDate } from "utilities/showDateUtils";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import { invalidateFlightsCache } from "utilities/api/flightQuery";
import { invalidateGroundTransportsCache } from "utilities/api/groundTransportQuery";
import { invalidateTrainsCache } from "utilities/api/trainQuery";

interface Props {
  show: server.dto.Show
  canLinkUnlinkTravel: boolean
  config: server.dto.LogisticsPanelConfig
}

const LinkedTravel = ({ show, canLinkUnlinkTravel, config }: Props) => {
  const planningContext = useContext(PlanningContext);
  const linkedTravelCacheKey = ["linkedTravel", show.showId];

  const linkedTravelQuery = useCustomQuery({
    queryKey: linkedTravelCacheKey,
    queryFn: async () => window.CoreApi.Show.GetLinkedTravel(show.showId).then((r) => r.data.data)
  });

  const queryClient = useQueryClient();

  const displayInAdvancingMutation = useMutation({
    mutationFn: async ({ appointment, displayInAdvancing }: { appointment: server.dto.AppointmentForList, displayInAdvancing: boolean }) => {
      const dto = { showId: show.showId, itemId: getLinkedTravelInfo(appointment).id, displayInAdvancing };
      switch (appointment.type) {
        case "flight":
          return window.CoreApi.Show.LinkFlight(dto);
        case "train":
          return window.CoreApi.Show.LinkTrain(dto);
        case "groundTransport":
          return window.CoreApi.Show.LinkGroundTransport(dto);
        default:
          throw new Error(`Unexpected linked travel type: ${appointment.type}`);
      }
    },
    onMutate: async ({ appointment, displayInAdvancing }) => {
      await queryClient.cancelQueries(linkedTravelCacheKey);
      const itemId = getLinkedTravelInfo(appointment).id;
      queryClient.setQueryData<server.dto.ShowToTravelAppointment[]>(linkedTravelCacheKey, (travels) => travels.map((t) =>
        t.appointment.type === appointment.type && getLinkedTravelInfo(t.appointment).id === itemId ? { ...t, displayInAdvancing } : t
      ));
    },
    onSettled: () => {
      queryClient.invalidateQueries(linkedTravelCacheKey);
    }
  });

  const setDisplayInAdvancing = (appointment: server.dto.AppointmentForList, displayInAdvancing: boolean) => {
    displayInAdvancingMutation.mutate({ appointment, displayInAdvancing });
  };

  const unlinkFlightMutation = useMutation({
    mutationFn: async (itemId: number) => window.CoreApi.Show.UnlinkFlight({ showId: show.showId, itemId }),
    onSuccess: () => {
      queryClient.invalidateQueries(linkedTravelCacheKey);
      invalidateFlightsCache(queryClient);
    }
  });

  const unlinkTrainMutation = useMutation({
    mutationFn: async (itemId: number) => window.CoreApi.Show.UnlinkTrainSegment({ showId: show.showId, itemId }),
    onSuccess: () => {
      queryClient.invalidateQueries(linkedTravelCacheKey);
      console.log("Invalidating trains cache");
      invalidateTrainsCache(queryClient);
    }
  });

  const unlinkGroundTransportMutation = useMutation({
    mutationFn: async (itemId: number) => window.CoreApi.Show.UnlinkGroundTransport({ showId: show.showId, itemId }),
    onSuccess: () => {
      queryClient.invalidateQueries(linkedTravelCacheKey);
      invalidateGroundTransportsCache(queryClient);
    }
  });

  const onNewAppointment = () => {
    const { startRealDate } = getRealStartEndDate({
      startDisplayDate: moment.utc(show.dateTime.startDisplay),
      endDisplayDate: moment.utc(show.dateTime.endDisplay),
      mode: show.dateTime.mode,
      timeOfPerformance: show.dateTime.customFallbackTime
    });

    planningContext.open({
      fromShowId: show.showId,
      date: startRealDate,
      artist: show.artist
    });
  };

  const totalItems = linkedTravelQuery.data ? linkedTravelQuery.data.length : 0;

  const { refToScrollTo } = useShortcutPanel({
    type: "itinerary",
    label: config.headerLabel,
    totalItems,
    onAddClicked: config.canAdd ? onNewAppointment : undefined
  });

  if (!linkedTravelQuery.data) {
    return <Panel
      panelClassName="s1_related-feature-panel"
      title={config.headerLabel}
      isLoading
      panelRef={refToScrollTo}
      hasError={linkedTravelQuery.isError}
    />;
  }

  const hasData = linkedTravelQuery.data.length > 0;
  const flights = linkedTravelQuery.data.filter((t) => t.appointment.type === "flight");
  const trains = linkedTravelQuery.data.filter((t) => t.appointment.type === "train");
  const groundTransports = linkedTravelQuery.data.filter((t) => t.appointment.type === "groundTransport");

  return (
    <Panel
      panelClassName="s1_related-feature-panel"
      title={config.headerLabel}
      panelRef={refToScrollTo}
      isLoading={linkedTravelQuery.isLoading}
      hasData={hasData}
      hasError={linkedTravelQuery.isError}
      contentCssClass="flex-column gap-larger"
      button={config.canAdd ? <ButtonSecondary onClick={onNewAppointment} label={RESX.Buttons.btnNew} /> : null}>

      <LinkedTravelRows travels={flights} icon={<PhosphorAirplaneTilt iconSize="large" />} title={RESX.Flight.Flights} unlink={unlinkFlightMutation.mutateAsync} setDisplayInAdvancing={setDisplayInAdvancing} canLinkUnlinkTravel={canLinkUnlinkTravel} />

      <LinkedTravelRows travels={trains} icon={<PhosphorTrain iconSize="large" />} title={RESX.Train.Trains} unlink={unlinkTrainMutation.mutateAsync} setDisplayInAdvancing={setDisplayInAdvancing} canLinkUnlinkTravel={canLinkUnlinkTravel} />

      <LinkedTravelRows travels={groundTransports} icon={<PhosphorCar iconSize="large" />} title={RESX.GroundTransport.resxGroundTransport} unlink={unlinkGroundTransportMutation.mutateAsync} setDisplayInAdvancing={setDisplayInAdvancing} canLinkUnlinkTravel={canLinkUnlinkTravel} />
    </Panel>
  );
};

export default LinkedTravel;